import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

type MiscPrimitiveState = string | number | boolean | null | undefined;
type MiscObjectState = Record<string, unknown>;
type MiscCollectionState = unknown[];
type MiscFeatureState = MiscPrimitiveState | MiscObjectState | MiscCollectionState;

/**
 * MiscFeatureStateService
 * --------------------------------------------------------------------
 * Grouped small feature streams that don't warrant their own file:
 *  - DTL data + errors
 *  - CLOlite data + errors
 *  - MinDimLevel data + errors
 *  - Flextune data + validity
 */
@Injectable({ providedIn: "root" })
export class MiscFeatureStateService {
  // --- DTL ---------------------------------------------------------
  readonly setDtlData = new BehaviorSubject<MiscFeatureState>("");
  readonly getDtldData: Observable<MiscFeatureState> = this.setDtlData.asObservable();
  readonly setMinMax = new BehaviorSubject<MiscFeatureState>("");
  readonly getMinMaxData: Observable<MiscFeatureState> = this.setMinMax.asObservable();
  readonly setdtlInputError = new BehaviorSubject<MiscFeatureState>("");
  readonly getdtlInputError: Observable<MiscFeatureState> = this.setdtlInputError.asObservable();
  readonly setDtlEdited = new BehaviorSubject<MiscFeatureState>("");
  readonly getDtlEdited: Observable<MiscFeatureState> = this.setDtlEdited.asObservable();

  // --- CLOlite -----------------------------------------------------
  readonly setCloLiteData = new BehaviorSubject<MiscFeatureState>("");
  readonly getCloLitedData: Observable<MiscFeatureState> = this.setCloLiteData.asObservable();
  readonly setCLOliteInputError = new BehaviorSubject<MiscFeatureState>("");
  readonly getCLOliteInputError: Observable<MiscFeatureState> =
    this.setCLOliteInputError.asObservable();

  // --- MinDimLevel -------------------------------------------------
  readonly setMinDimLevelData = new BehaviorSubject<MiscFeatureState>("");
  readonly getMinDimLevelData: Observable<MiscFeatureState> = this.setMinDimLevelData.asObservable();
  readonly setminDimLevelInputError = new BehaviorSubject<MiscFeatureState>("");
  readonly getminDimLevelInputError: Observable<MiscFeatureState> =
    this.setminDimLevelInputError.asObservable();
  readonly setMinDimLevelEdited = new BehaviorSubject<MiscFeatureState>("");
  readonly getMinDimLevelEdited: Observable<MiscFeatureState> = this.setMinDimLevelEdited.asObservable();

  // --- Flextune ----------------------------------------------------
  readonly flextuneData = new BehaviorSubject<MiscFeatureState>("");
  readonly getFlextuneData: Observable<MiscFeatureState> = this.flextuneData.asObservable();
  readonly isFlextuneValid = new BehaviorSubject<MiscFeatureState>("");
  readonly getFlextuneValid: Observable<MiscFeatureState> = this.isFlextuneValid.asObservable();

  // --- Setters ----------------------------------------------------
  sendDtldata(data: MiscFeatureState): void {
    this.setDtlData.next(data);
  }
  sendMinMaxValues(data: MiscFeatureState): void {
    this.setMinMax.next(data);
  }
  dtlInputError(data: MiscFeatureState): void {
    this.setdtlInputError.next(data);
  }
  dtlEditedVal(data: MiscFeatureState): void {
    this.setDtlEdited.next(data);
  }

  sendCloLitedata(data: MiscFeatureState): void {
    this.setCloLiteData.next(data);
  }
  cloLiteInputError(data: MiscFeatureState): void {
    this.setCLOliteInputError.next(data);
  }

  sendMinDimLeveldata(data: MiscFeatureState): void {
    this.setMinDimLevelData.next(data);
  }
  minDimLevelInputError(data: MiscFeatureState): void {
    this.setminDimLevelInputError.next(data);
  }
  MinDimLevelEditedVal(data: MiscFeatureState): void {
    this.setMinDimLevelEdited.next(data);
  }

  sendFlextuneData(data: MiscFeatureState): void {
    this.flextuneData.next(data);
  }
  sendFlextuneValidity(data: MiscFeatureState): void {
    this.isFlextuneValid.next(data);
  }
}
